import { useCallback, useState } from "react";

import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { RealTimeNotificationState } from "@/Common/Api/RealTimeNotification/RealTimeNotificationState";
import { realTimeNotificationClient, RealTimeNotificationEventType } from "@/Common/Api/RealTimeNotification/SignalRClient";
import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { useRealTimeNotificationEvents } from "@/Common/CustomHooks/useRealTimeNotificationEvents";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CallCenterHelper } from "@/Common/Helpers/CallCenterHelper";
import { CallCenterUserConnectionState } from "@/Common/Models/CallCenter/CallCenterUserConnectionState";
import { IconBadge, LinkButton } from "@/DesignSystem";

import styles from "./CallCenterSettings.module.scss";

enum CallCenterDisplayState {
    Disconnected = "Disconnected",
    Connected = "Connected",
    Connecting = "Connecting"
}

function GetCallCenterDisplayState(callCenterState: CallCenterUserConnectionState, realTimeState: RealTimeNotificationState): CallCenterDisplayState {
    if (callCenterState === CallCenterUserConnectionState.Disconnected
        || realTimeState === RealTimeNotificationState.Disconnected
        || realTimeState === RealTimeNotificationState.Disconnecting) {
        return CallCenterDisplayState.Disconnected;
    }
    else if (callCenterState === CallCenterUserConnectionState.Connecting
        || realTimeState === RealTimeNotificationState.Connecting
        || realTimeState === RealTimeNotificationState.Reconnecting) {
        return CallCenterDisplayState.Connecting;
    }
    return CallCenterDisplayState.Connected;
}

function GetStateIconFromState(state: Readonly<CallCenterDisplayState>): JSX.Element {
    switch (state) {
        case CallCenterDisplayState.Connected:
            return <IconBadge variant="success" size="sm" dataCy="CallCenter_State_Icon" />;
        case CallCenterDisplayState.Disconnected:
            return <IconBadge variant="error" size="sm" dataCy="CallCenter_State_Icon" />;
        case CallCenterDisplayState.Connecting:
            return <IconBadge variant="warning" size="sm" dataCy="CallCenter_State_Icon" />;
        default:
            return <IconBadge variant="error" size="sm" dataCy="CallCenter_State_Icon" />;
    }
}

function GetStateVocabFromState(state: CallCenterDisplayState): string {
    switch (state) {
        case CallCenterDisplayState.Connected:
            return "CallCenter_State_Connected";
        case CallCenterDisplayState.Disconnected:
            return "CallCenter_State_Disconnected";
        case CallCenterDisplayState.Connecting:
            return "CallCenter_State_Connecting";
        default:
            return "CallCenter_State_Disconnected";
    }
}

function GetActionVocabFromState(state: CallCenterDisplayState): string {
    switch (state) {
        case CallCenterDisplayState.Connected:
            return "CallCenter_Action_Disconnect";
        case CallCenterDisplayState.Disconnected:
            return "CallCenter_Action_Connect";
        case CallCenterDisplayState.Connecting:
            return "CallCenter_Action_Disconnect";
        default:
            return "CallCenter_Action_Disconnect";
    }
}

function GetStateHoverText(displayState: CallCenterDisplayState, realTimeState: RealTimeNotificationState, lastLoginErrors: ErrorMessage[]): string {
    if (displayState !== CallCenterDisplayState.Connecting) {
        return null;
    }

    let errors = lastLoginErrors ?? [];
    if (realTimeState !== RealTimeNotificationState.Connected) {
        errors = [...errors, new ErrorMessage({ VocabularyKey: "CallCenter_RealTime_Error" })];
    }

    const message = sessionStore.formatErrorsForDisplay(errors).join("\n");
    if (!message) {
        return null;
    }
    return message;
}

function CallCenterLoginAndStatus(): JSX.Element {
    const { getText } = useTranslation();
    const [realTimeState, setRealTimeState] = useState<RealTimeNotificationState>(realTimeNotificationClient.state);
    const [callCenterState, setCallCenterState] = useState<CallCenterUserConnectionState>(sessionStore.CallCenter_UserConnectionState);
    const [callCenterLoginErrors, setCallCenterLoginErrors] = useState<ErrorMessage[]>(sessionStore.CallCenter_LastLoginError ?? []);

    useRealTimeNotificationEvents(RealTimeNotificationEventType.StateChanged, realTimeClient => {
        setRealTimeState(realTimeClient.state);
    }, []);

    useOnStoreChange(sessionStore, () => {
        setCallCenterState(sessionStore.CallCenter_UserConnectionState);
        setCallCenterLoginErrors(sessionStore.CallCenter_LastLoginError ?? []);
    });

    const displayState = GetCallCenterDisplayState(callCenterState, realTimeState);

    const onActionClick = useCallback(() => {
        if (displayState === CallCenterDisplayState.Disconnected) {
            CallCenterHelper.loginAsync({ isUserInitiated: true });
        }
        else {
            CallCenterHelper.logoutAsync();
        }
    }, [displayState]);

    const stateIcon = GetStateIconFromState(displayState);
    const stateText = getText(GetStateVocabFromState(displayState));

    const stateHoverText = GetStateHoverText(displayState, realTimeState, callCenterLoginErrors);
    const actionButtonText = getText(GetActionVocabFromState(displayState));

    const statusStyle = displayState === CallCenterDisplayState.Disconnected ? styles.disconnected : styles.connected;

    return (
        <div className={styles.connectionStatus}>
            <div className={styles.status} title={stateHoverText} data-cy="CallCenterConnectionStatus">
                {stateIcon}
                <span className={statusStyle}>{stateText}</span>
            </div>
            <div className={styles.actionButton}>
                <LinkButton label={actionButtonText} onClick={onActionClick} underline={false} dataCy="CallCenterActionButton" />
            </div>
        </div>
    );
}

export { CallCenterLoginAndStatus };
